import { useContext } from 'react';

import { RoutesContext } from '@confluence/route-manager';
import { useIsMobileApp } from '@confluence/mobile-app-detection';
import {
	WHITEBOARD_LINKED_OBJECT,
	WHITEBOARD_LOCALE,
	HEADLESS_QUERY_PARAM,
	FOCUSED_COMMENT_ID,
	REPLY_TO_COMMENT,
	PARENT_PRODUCT_QUERY_PARAM,
	CREATED_FROM_SLUG,
	VISUAL_REFRESH_PARAM,
} from '@confluence/whiteboard-utils';
import type { ParsedUrlQuery } from '@confluence/route-manager-utils';

import { whiteboardSpotFromParams } from './whiteboard-spot-from-params';

export type GetWhiteboardParamsResult = {
	'canvas-frontend': string | null;
	'whiteboard-locale': string | undefined;
	'whiteboard-linked-object': string | undefined;
	'canvas-server': number | undefined;
	'canvas-relay': number | undefined;
	focusedCommentId: string | undefined;
	replyToComment: string | undefined;
	headless: boolean;
	parentProduct: string | undefined;
	createdFromSlug: string | undefined;
	/**
	 * "Embedded" refers to whiteboards showing in an embedded iframe - Generally used as a smart link
	 * @see {@link https://fabric-in-confluence.jira-dev.com/wiki/spaces/TESTFIXED/whiteboard/72080365&parentProduct=smartLink} is an example of an embed - Although normally this link would be an iframe inside of another content (E.g. An embed within a page or another board)
	 *
	 * Note that this will only be true for smart link embeds, so this does not include the embed they show in JSW
	 */
	isSmartLinkEmbed: boolean;
	isMobileAppEmbed: boolean;
	visualRefresh: boolean;
};

const envList = ['dev', 'branch', 'staging', 'hello'];

const retrieveQueryParameter = (queryParams: ParsedUrlQuery, key: string): string | undefined => {
	return queryParams?.[key] && !Array.isArray(queryParams?.[key])
		? (queryParams?.[key] as string) // TypeScript doesn't acknowledge the !Array.isArray
		: undefined;
};

export const useWhiteboardParams = (): GetWhiteboardParamsResult => {
	const { isMobileApp: isMobileAppEmbed } = useIsMobileApp();
	const { getQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();

	// Versioned deploys are currently only supported in development and internal
	// environments
	const allowDevParams = envList.some((e) => e === process.env.CLOUD_ENV);

	const frontendVersion =
		allowDevParams &&
		queryParams?.['canvas-frontend'] &&
		!Array.isArray(queryParams?.['canvas-frontend'])
			? queryParams?.['canvas-frontend']
			: null;

	const whiteboardLocale = retrieveQueryParameter(queryParams, WHITEBOARD_LOCALE);
	const whiteboardLinkedObject = retrieveQueryParameter(queryParams, WHITEBOARD_LINKED_OBJECT);

	const canvasServerSpot = allowDevParams
		? whiteboardSpotFromParams(queryParams, 'canvas-server')
		: undefined;

	const canvasRelaySpot = allowDevParams
		? whiteboardSpotFromParams(queryParams, 'canvas-relay')
		: undefined;

	const focusedCommentId = retrieveQueryParameter(queryParams, FOCUSED_COMMENT_ID);
	const replyToComment = retrieveQueryParameter(queryParams, REPLY_TO_COMMENT);
	const parentProduct = retrieveQueryParameter(queryParams, PARENT_PRODUCT_QUERY_PARAM);
	const createdFromSlug = retrieveQueryParameter(queryParams, CREATED_FROM_SLUG);
	const visualRefresh = retrieveQueryParameter(queryParams, VISUAL_REFRESH_PARAM);

	return {
		'canvas-frontend': frontendVersion,
		'canvas-server': canvasServerSpot,
		'canvas-relay': canvasRelaySpot,
		[WHITEBOARD_LOCALE]: whiteboardLocale,
		[WHITEBOARD_LINKED_OBJECT]: whiteboardLinkedObject,
		[FOCUSED_COMMENT_ID]: focusedCommentId,
		[REPLY_TO_COMMENT]: replyToComment,
		[CREATED_FROM_SLUG]: createdFromSlug,
		headless: queryParams?.[HEADLESS_QUERY_PARAM] === 'true',
		parentProduct,
		isSmartLinkEmbed: parentProduct === 'smartLink',
		isMobileAppEmbed,
		[VISUAL_REFRESH_PARAM]: visualRefresh === 'true',
	};
};
